/**
 * Premium /blog page content — metrics, filters, featured stack, service playbooks.
 */
import { blogPosts } from './blogPosts';
import type { BlogPost } from '../types/blog';

export type BlogTopicFilterId =
  | 'all'
  | 'performance'
  | 'ecommerce'
  | 'crm'
  | 'analytics'
  | 'education'
  | 'wordpress';

export type BlogProofMetric = {
  id: string;
  value: string;
  label: string;
  detail: string;
};

export type BlogTopicFilter = {
  id: BlogTopicFilterId;
  label: string;
};

export type BlogServicePlaybook = {
  id: string;
  title: string;
  summary: string;
  serviceHref: string;
  serviceLabel: string;
  articleSlugs: string[];
};

export type FeaturedInsightStack = {
  lead: BlogPost;
  secondary: BlogPost[];
};

export const HERO_INSIGHT_NODES = [
  'Performance',
  'Ecommerce',
  'CRM',
  'Analytics',
  'Education',
  'WordPress',
  'SEO',
  'Integrations',
];

export const BLOG_TOPIC_FILTERS: BlogTopicFilter[] = [
  { id: 'all', label: 'All insights' },
  { id: 'performance', label: 'Performance & maintenance' },
  { id: 'ecommerce', label: 'Ecommerce & Magento/WooCommerce' },
  { id: 'crm', label: 'CRM, forms & integrations' },
  { id: 'analytics', label: 'Analytics, GTM & privacy' },
  { id: 'education', label: 'Education platforms & search' },
  { id: 'wordpress', label: 'WordPress, Sage & CMS' },
];

const THEME_FILTER_MAP: Record<string, BlogTopicFilterId> = {
  performance: 'performance',
  ecommerce: 'ecommerce',
  marketplace: 'ecommerce',
  erp: 'ecommerce',
  'forms-crm': 'crm',
  analytics: 'analytics',
  privacy: 'analytics',
  education: 'education',
  wordpress: 'wordpress',
  'web-product': 'performance',
  'specialized-delivery': 'performance',
};

const CATEGORY_FILTER_MAP: Record<string, BlogTopicFilterId> = {
  'Web Development': 'performance',
  Ecommerce: 'ecommerce',
  WooCommerce: 'ecommerce',
  'Marketplace SEO': 'ecommerce',
  'CRM Integration': 'crm',
  Analytics: 'analytics',
  'Privacy & Widgets': 'analytics',
  'Education Search': 'education',
  WordPress: 'wordpress',
};

const serviceLinkedCount = blogPosts.filter(
  (post) => post.relatedServiceLinks && post.relatedServiceLinks.length > 0,
).length;

const featuredCount = blogPosts.filter((post) => post.featured).length;

const uniqueCategories = new Set(blogPosts.map((post) => post.category));

export const BLOG_PROOF_METRICS: BlogProofMetric[] = [
  {
    id: 'articles',
    value: String(blogPosts.length),
    label: 'production articles',
    detail: 'Structured guides on web development, ecommerce, CRM, analytics, education search, and CMS delivery.',
  },
  {
    id: 'categories',
    value: String(uniqueCategories.size),
    label: 'topic categories',
    detail: 'Performance, ecommerce, integrations, analytics, education platforms, WordPress, and marketplace SEO.',
  },
  {
    id: 'service-linked',
    value: String(serviceLinkedCount),
    label: 'service-linked guides',
    detail: 'Articles mapped to web development, maintenance, ecommerce, and integration service pathways.',
  },
  {
    id: 'featured',
    value: String(featuredCount),
    label: 'featured insights',
    detail: 'Editorial highlights on Core Web Vitals, CRM workflows, GTM, education search, and WooCommerce.',
  },
];

export const BLOG_SERVICE_PLAYBOOKS: BlogServicePlaybook[] = [
  {
    id: 'web-development',
    title: 'Web development & performance',
    summary: 'Core Web Vitals, SSR, React/Next.js, landing pages, and production maintenance playbooks.',
    serviceHref: '/web-development',
    serviceLabel: 'Web development services',
    articleSlugs: [
      'core-web-vitals-optimization-checklist',
      'server-side-rendering-seo-react-nextjs',
      'nextjs-web-applications-saas-product-teams',
      'seo-friendly-landing-pages-service-businesses',
      'secure-wordpress-maintenance-checklist',
    ],
  },
  {
    id: 'ecommerce',
    title: 'Ecommerce & Magento/WooCommerce',
    summary: 'Store architecture, checkout flows, CRM sync, marketplace SEO, and order/customer integrations.',
    serviceHref: '/ecommerce-development',
    serviceLabel: 'Ecommerce development',
    articleSlugs: [
      'magento-ecommerce-development-store-architecture',
      'ecommerce-seo-checklist-woocommerce-magento',
      'woocommerce-crm-integration-guide',
      'sync-woocommerce-orders-customers-crm-inventory',
      'marketplace-product-listing-seo-amazon-flipkart',
    ],
  },
  {
    id: 'integrations',
    title: 'CRM, forms & integrations',
    summary: 'Salesforce, HubSpot, Marketo, FormAssembly, Calendly, and lead workflow integration guides.',
    serviceHref: '/form-crm-integrations',
    serviceLabel: 'CRM & form integrations',
    articleSlugs: [
      'connect-website-forms-salesforce-lead-data',
      'calendly-hubspot-website-forms-lead-workflows',
      'formassembly-marketo-integration-lead-generation',
      'crm-integration-mistakes-sales-reporting',
      'wordpress-plugin-integrations-forms-crm-analytics',
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics, GTM & privacy',
    summary: 'Tag Manager setup, dataLayer tracking, consent mode, UX analytics, and widget governance.',
    serviceHref: '/services',
    serviceLabel: 'Analytics & tracking support',
    articleSlugs: [
      'google-tag-manager-setup-checklist',
      'datalayer-gtm-form-conversion-tracking',
      'onetrust-consent-mode-privacy-tracking',
      'hotjar-clarity-ux-analytics-what-to-track',
      'trustpilot-unibuddy-google-maps-website-widgets',
    ],
  },
  {
    id: 'education',
    title: 'Education platforms & search',
    summary: 'Course finder SEO, program schema, campus pages, Algolia/SiteSearch360, and large-site search.',
    serviceHref: '/education-website-development',
    serviceLabel: 'Education website development',
    articleSlugs: [
      'university-course-finder-seo',
      'seo-strategy-program-course-campus-pages',
      'schema-markup-education-program-listings',
      'algolia-sitesearch360-fusejs-website-search',
      'fast-search-filter-large-content-websites',
    ],
  },
  {
    id: 'wordpress',
    title: 'WordPress, Sage & CMS',
    summary: 'Sage theme development, ACF flexible content, plugin integrations, and editorial workflow.',
    serviceHref: '/wordpress-development',
    serviceLabel: 'WordPress development',
    articleSlugs: [
      'roots-sage-wordpress-theme-development',
      'acf-flexible-content-sections-marketing-websites',
      'improve-wordpress-performance-editorial-workflow',
      'react-vs-wordpress-seo',
      'woocommerce-checkout-flywire-invoicing',
    ],
  },
];

export function getSortedBlogPosts(): BlogPost[] {
  return [...blogPosts].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
}

export function getPostTopicFilterId(post: BlogPost): BlogTopicFilterId {
  return (
    THEME_FILTER_MAP[post.homepageTheme] ||
    CATEGORY_FILTER_MAP[post.category] ||
    'performance'
  );
}

export function postMatchesFilter(
  post: BlogPost,
  filterId: BlogTopicFilterId,
): boolean {
  if (filterId === 'all') return true;
  return getPostTopicFilterId(post) === filterId;
}

export function getFilteredBlogPosts(filterId: BlogTopicFilterId): BlogPost[] {
  return getSortedBlogPosts().filter((post) => postMatchesFilter(post, filterId));
}

export function getFeaturedInsightStack(): FeaturedInsightStack {
  const sorted = getSortedBlogPosts();
  const featured = sorted.filter((post) => post.featured);
  const source = featured.length >= 3 ? featured : sorted;
  const [lead, ...rest] = source;

  return {
    lead,
    secondary: rest.slice(0, 3),
  };
}

export function getBlogPostHref(post: BlogPost): string {
  return `/blogs/${post.slug}`;
}

export function formatBlogDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return isoDate;
  return parsed.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function getTopicBadgeLabel(post: BlogPost): string {
  return post.category || post.homepageTheme;
}

export function getVisualGlyph(category: string): string {
  const words = category.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return category.slice(0, 2).toUpperCase();
}

export function getVisualBadgeClass(post: BlogPost): string {
  const filterId = getPostTopicFilterId(post);
  return `blog-premium-card__visual blog-premium-card__visual--${filterId}`;
}

export function getFeaturedVisualBadgeClass(post: BlogPost): string {
  const filterId = getPostTopicFilterId(post);
  return `blog-premium-featured__visual blog-premium-featured__visual--${filterId}`;
}

export function slugifyHeading(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function getSectionAnchorId(heading: string, index: number): string {
  const slug = slugifyHeading(heading);
  return slug ? `section-${slug}` : `section-${index + 1}`;
}

export function getPlaybookPosts(playbook: BlogServicePlaybook): BlogPost[] {
  return playbook.articleSlugs
    .map((slug) => blogPosts.find((post) => post.slug === slug))
    .filter((post): post is BlogPost => Boolean(post));
}
